import {
	type BaseState,
	type GameDefinition,
	type MoveOutcome,
	type PlayerIndex,
	reject,
	resultFromScores,
} from "../types";

export const REFLEX_ROUNDS = 5;
/** The wait before "GO!" is random, so nobody can learn the rhythm. */
export const REFLEX_MIN_WAIT_MS = 1500;
export const REFLEX_MAX_WAIT_MS = 5000;
export const REFLEX_ROUND_TIMEOUT_MS = 4000;
export const REFLEX_REVEAL_MS = 2600;

export interface ReflexState extends BaseState {
	kind: "reflex";
	round: number;
	phase: "wait" | "go" | "reveal";
	/** When the light turns green, the round times out, or the reveal ends. */
	endsAt: number;
	/**
	 * The instant the light turned green, which is what a reaction is measured
	 * from. Working it back out of `endsAt` gave whoever's own message triggered
	 * the transition a reaction of exactly zero — the server evaluates the tick
	 * and the move at the same `now`, so the subtraction cancelled out.
	 */
	goAt: number | null;
	/**
	 * How long each round's red light lasts, all drawn from the rng in `create`.
	 *
	 * Deriving the later rounds' waits from the round number instead made them
	 * the same in every game ever played — a fixed rhythm dressed up as a random
	 * one. They are secrets in the same way `endsAt` is, so `redact` strips them.
	 */
	waits: number[];
	/** Tap times relative to "GO!", or `-1` for a false start. */
	taps: [number | null, number | null];
	lastRound: { winner: PlayerIndex | null; because: string } | null;
	/** Best reaction so far, in milliseconds. */
	best: [number | null, number | null];
}

export interface ReflexMove {
	tap: true;
}

function waitFor(state: ReflexState, now: number): ReflexState {
	return {
		...state,
		phase: "wait",
		endsAt: now + state.waits[state.round],
		goAt: null,
		taps: [null, null],
		lastRound: null,
	};
}

function judge(state: ReflexState, now: number): ReflexState {
	const [first, second] = state.taps;
	const scores: [number, number] = [...state.scores];
	let lastRound: ReflexState["lastRound"];

	const valid = (tap: number | null) => tap !== null && tap >= 0;

	if (valid(first) && valid(second)) {
		const winner: PlayerIndex = (first as number) < (second as number) ? 0 : 1;
		scores[winner] += 1;
		lastRound = { winner, because: `${Math.min(first as number, second as number)}ms — quickest!` };
	} else if (valid(first)) {
		scores[0] += 1;
		lastRound = { winner: 0, because: second === -1 ? "Too early!" : "Too slow!" };
	} else if (valid(second)) {
		scores[1] += 1;
		lastRound = { winner: 1, because: first === -1 ? "Too early!" : "Too slow!" };
	} else {
		lastRound = { winner: null, because: "Nobody got that one!" };
	}

	const best: [number | null, number | null] = [0, 1].map((seat) => {
		const tap = state.taps[seat as PlayerIndex];
		const previous = state.best[seat as PlayerIndex];
		if (tap === null || tap < 0) return previous;
		return previous === null ? tap : Math.min(previous, tap);
	}) as [number | null, number | null];

	return { ...state, scores, best, lastRound, phase: "reveal", endsAt: now + REFLEX_REVEAL_MS };
}

export const reflexTap: GameDefinition<ReflexState, ReflexMove> = {
	id: "reflex",
	category: "racing",
	title: "Reflex Tap",
	emoji: "🚦",
	blurb: "Wait for green, then tap first. Don't jump the light!",
	howTo: [
		"The screen goes red — wait.",
		"The moment it turns green, tap!",
		"Tap too early and you lose the round.",
		"Most rounds out of five wins.",
	],

	create(rng, now) {
		return waitFor(
			{
				kind: "reflex",
				round: 0,
				phase: "wait",
				endsAt: now,
				goAt: null,
				waits: Array.from(
					{ length: REFLEX_ROUNDS },
					() =>
						REFLEX_MIN_WAIT_MS + Math.floor(rng.next() * (REFLEX_MAX_WAIT_MS - REFLEX_MIN_WAIT_MS)),
				),
				taps: [null, null],
				lastRound: null,
				best: [null, null],
				// Both players are racing at once.
				turn: null,
				scores: [0, 0],
				result: null,
			},
			now,
		);
	},

	parseMove(raw) {
		if (typeof raw !== "object" || raw === null) return null;
		return (raw as { tap?: unknown }).tap === true ? { tap: true } : null;
	},

	apply(state, player, _move, now): MoveOutcome<ReflexState> {
		if (state.result) return reject("This game is already finished.");
		if (state.phase === "reveal") return reject("Hold on — the round is finishing.");
		if (state.taps[player] !== null) return reject("You've already tapped.");

		const taps: [number | null, number | null] = [...state.taps];
		// A tap while the light is still red is a false start.
		taps[player] =
			state.phase === "wait" || state.goAt === null ? -1 : Math.max(0, now - state.goAt);

		const next = { ...state, taps };
		return {
			ok: true,
			state: taps[0] !== null && taps[1] !== null ? judge(next, now) : next,
		};
	},

	legalMoves(state, player) {
		if (state.result || state.phase === "reveal" || state.taps[player] !== null) return [];
		return [{ tap: true } as ReflexMove];
	},

	tick(state, now) {
		if (state.result || now < state.endsAt) return null;

		if (state.phase === "wait") {
			// Green light: the round is now a race.
			return { ...state, phase: "go", goAt: now, endsAt: now + REFLEX_ROUND_TIMEOUT_MS };
		}
		if (state.phase === "go") return judge(state, now);

		const round = state.round + 1;
		if (round >= REFLEX_ROUNDS) {
			return { ...state, result: resultFromScores(state.scores), endsAt: now };
		}
		return waitFor({ ...state, round }, now);
	},

	nextTickAt(state) {
		return state.result ? null : state.endsAt;
	},

	redact(state, viewer) {
		// The stored waits are every red light still to come, to the millisecond
		// — the same secret as `endsAt`, five rounds at once — so they never go
		// out in any phase.
		const hidden = { ...state, waits: [] };
		if (state.phase === "wait") {
			// While the light is red, `endsAt` *is* the answer: it says to the
			// millisecond when green arrives, and `serverNow` supplies the offset
			// to aim with. A scripted tap would book a perfect reaction every
			// round. The opponent's tap is hidden entirely rather than masked:
			// the only tap possible under a red light is a false start, so even
			// "they have tapped" says the round is already unlosable.
			return {
				...hidden,
				endsAt: 0,
				goAt: null,
				taps: state.taps.map((tap, index) => (index === viewer ? tap : null)) as [
					number | null,
					number | null,
				],
			};
		}
		if (state.phase !== "go") return hidden;
		// Don't show that they've already tapped — it would tempt a panic tap.
		return {
			...hidden,
			taps: state.taps.map((tap, index) => (index === viewer ? tap : tap === null ? null : -2)) as [
				number | null,
				number | null,
			],
		};
	},
};
